import { html } from '@acme/b2b/utils';
import { GridStateFactory } from '@b2b/common/factories/gridState/types';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { PermissionsService } from '@b2b/common/services/permissions';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { ColumnDef, TODO, UIBModalService } from '@b2b/types/core';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IPromise, IScope, ITimeoutService } from 'angular';
import { ProgramsCombosCreateModalControllerModule } from '../../controllers/PricingCombosCreateModalController';
import { ProgramsCombosRemoveModalControllerModule } from '../../controllers/ProgramsCombosRemoveModalController';
import { getComboStatus } from '../../utils/combo-status';

ProgramsCombosListController.$inject = [
  '$scope',
  '$state',
  '$timeout',
  '$filter',
  '$uibModal',
  'HelpersService',
  'ApiService',
  'PermissionsService',
  'CalendarService',
  'ValidateService',
  'NoticeFactory',
  'GridStateFactory',
  'UserPreferencesService',
];

function ProgramsCombosListController(
  $scope: IScope & { programsCombosList: TODO },
  $state: StateService,
  $timeout: ITimeoutService,
  $filter: IFilterService,
  $uibModal: UIBModalService,
  HelpersService: TODO,
  ApiService: ApiService,
  PermissionsService: PermissionsService,
  CalendarService: CalendarService,
  ValidateService: TODO,
  NoticeFactory: NoticeFactory,
  GridStateFactory: GridStateFactory,
  UserPreferencesService: UserPreferencesService,
) {
  const ctrl = {
    model: [] as TODO[],
    filtered: [] as TODO[],
    searchQuery: '',
    searchTimer: null as IPromise<void> | null,
    isLoaded: false,
    createModal: {},
    removeModal: {},
    notice: new NoticeFactory(),
    gridState: new GridStateFactory(),
    dateFormat: UserPreferencesService.getDateFormat('fieldDate', 'dateFormat'),
    grid: {
      multiselect: false,
      enableRowSelection: false,
      paginate: true,
      pageSize: 50,
      showFooter: true,
      rowHeight: 32,
      useExternalPagination: true,
      useExternalSorting: true,
      totalItems: 0,
      columnDefs: [
        {
          field: 'edit',
          displayName: 'Edit',
          width: 50,
          onCellNavClick: (row, col) => {
            ctrl.onGridDblClick(row);
          },
          cellTemplate: html`
            <div class="ui-grid-cell-contents">
              <a data-permit="COMBO_TEMPLATE_UPDATE" data-ng-click="grid.appScope.onGridDblClick(row)" role="link">
                Edit<span class="sr-only"> Details for {{ row.entity.name }}</span>
              </a>
            </div>
          `,
          visible: true,
          enableHiding: false,
          enableSorting: false,
        },
        {
          field: 'id',
          displayName: 'Combo Id',
          visible: false,
        },
        {
          field: 'name',
          displayName: 'Name',
        },
        {
          field: 'displayDescription',
          displayName: 'Description',
        },
        {
          field: 'dates',
          displayName: 'Dates',
        },
        {
          field: 'status',
          displayName: 'Status',
        },
      ] as ColumnDef[],
    },

    init: function () {
      $scope.$emit('Application::contentTitleChange', {
        title: 'Combos',
        buttonRight: {
          label: '+ Create Combo',
          permissions: 'COMBO_TEMPLATE_CREATE',
          action: () => {
            ctrl.create();
          },
        },
      });

      if (PermissionsService.check('COMBO_TEMPLATE_DELETE')) {
        ctrl.grid.columnDefs.push({
          field: 'remove',
          displayName: 'Remove',
          onCellNavClick: (row, col) => {
            ctrl.remove(row.entity.id);
          },
          cellTemplate: html`
            <div class="ui-grid-cell-contents">
              <a
                class="programs-combos-link programs-combos-link-remove"
                data-ng-click="grid.appScope.remove(row.entity.id)"
                role="button"
              >
                Remove<span class="sr-only">-Opens a dialog</span>
              </a>
            </div>
          `,
        });
      }

      ctrl.gridState.updateGridState({ pageSize: ctrl.grid.pageSize, sortField: 'name', sortDirection: 'asc' });

      $scope.$on('$destroy', $scope.$watch('programsCombosList.searchQuery', ctrl.onSearchQueryUpdate));

      ctrl.getProgramsCombosList();
    },

    getProgramsCombosList: function () {
      const props = Object.assign({}, ctrl.gridState.getGridState(), { summary: true, search: ctrl.searchQuery });

      ApiService.combo.templates
        .list(props)
        .then((response) => ctrl.success.getProgramsCombosList(response))
        .catch((response) => ctrl.error.getProgramsCombosList(response));
    },

    hasCombos: function () {
      return ctrl.model instanceof Array && ctrl.model.length > 0;
    },

    findCombo: function (id) {
      let r: { index: number; instance: TODO } | false = false;

      for (let i = 0, ii = ctrl.model.length; i < ii; i++) {
        if (ctrl.model[i].id === id) {
          r = {
            index: i,
            instance: ctrl.model[i],
          };
          break;
        }
      }

      return r;
    },

    search: function () {
      ctrl.gridState.updateGridState({ page: 1 });
      ctrl.getProgramsCombosList();
    },

    create: function () {
      $uibModal
        .open({
          templateUrl: 'views/application/views/programs/combos/modals/createCombo.tpl.html',
          size: 'md',
          controller: 'ProgramsCombosCreateModalController',
          resolve: {
            combosList: () => ctrl.model,
          },
        })
        .result.then((combo) => {
          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();

          ApiService.combo.templates
            .create(combo)
            .then((response) => ctrl.success.create(response))
            .catch((response) => ctrl.error.create(response));
        })
        .catch(() => {
          // noop
        });
    },

    remove: function (id) {
      const combo = ctrl.findCombo(id);

      if (combo) {
        $uibModal
          .open({
            templateUrl: 'views/application/views/programs/combos/modals/removeCombo.tpl.html',
            size: 'md',
            controller: 'ProgramsCombosRemoveModalController',
            resolve: {
              currentCombo: () => combo.instance,
            },
          })
          .result.then(() => {
            ctrl.notice.hideSuccessMessages();
            ctrl.notice.hideErrorMessages();

            ctrl.model.splice(combo.index, 1);
            ctrl.filtered = ctrl.model;

            ApiService.combo.templates
              .remove(combo.instance.id)
              .then(() => ctrl.getProgramsCombosList())
              .catch((response) => ctrl.error.remove(response));
          })
          .catch(() => {
            // noop
          });
      }
    },

    process: function (combos) {
      for (let i = 0, ii = combos.length; i < ii; i++) {
        const endTime = CalendarService.parseDate(combos[i].endTime);
        const startTime = CalendarService.parseDate(combos[i].startTime);

        combos[i].dates = CalendarService.toFormat(startTime, ctrl.dateFormat);
        combos[i].dates += ' - ';
        combos[i].dates += CalendarService.toFormat(endTime, ctrl.dateFormat);
        combos[i].displayDescription = combos[i].shortDescription
          ? HelpersService.strip(combos[i].shortDescription, 80)
          : HelpersService.strip(combos[i].description, 80);

        combos[i].status = getComboStatus({
          startTime,
          endTime,
          priceList: combos[i].priceList,
          discountId: combos[i].discountId,
          now: new Date(),
        });
      }

      return combos;
    },

    success: {
      init: function (response) {},
      create: function (response) {
        $state.go('application.programs.combos.edit.description', { comboId: response.data.payload.id });
      },
      remove: function (response) {},

      getProgramsCombosList: function (response) {
        ctrl.model = ctrl.process(response.data.payload.list);
        ctrl.filtered = ctrl.model;
        ctrl.grid.totalItems = response.data.payload.pagination.count;
        ctrl.isLoaded = true;
      },
    },

    error: {
      init: function (response) {},
      remove: function (response) {},
      create: function (response) {},
      getProgramsCombosList: function (response) {},
    },

    onGridDblClick: function (item) {
      if (PermissionsService.check('COMBO_TEMPLATE_UPDATE')) {
        // Create and edit are handled by the same controller
        $state.go('application.programs.combos.edit.description', { comboId: item.entity.id });
      }
    },

    onDataGridPaginate: function (newPage, pageSize) {
      ctrl.gridState.updateGridState({ page: newPage, pageSize });
      ctrl.getProgramsCombosList();
    },

    onSearchQueryUpdate: function (n, o) {
      if (n !== o) {
        if (ctrl.searchTimer) {
          $timeout.cancel(ctrl.searchTimer);
        }
        ctrl.searchTimer = $timeout(() => {
          ctrl.search();
        }, 250);
      }
    },

    onDataGridSortChange: function (grid, columns) {
      const data = columns[0];
      if (data) {
        ctrl.gridState.updateGridState({ sortField: data.field, sortDirection: data.sort.direction });
        ctrl.getProgramsCombosList();
      }
    },
  };

  $scope.programsCombosList = ctrl;
  ctrl.init();
}

export const ProgramsCombosListControllerModule = angular
  .module('acme.view.application.programs.combos.list.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.helpers',
    'acme.common.service.calendar',
    'acme.common.service.validate',
    'acme.common.service.permissions',
    'acme.common.factory.notice',
    ProgramsCombosRemoveModalControllerModule,
    ProgramsCombosCreateModalControllerModule,
    'acme.common.factory.gridState',
    'acme.common.service.userPreferences',
  ])
  .controller('ProgramsCombosListController', ProgramsCombosListController).name;
